"use client";
import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";
import { fmt } from "./helpers";
import { getIconByTitle, resolveTrackTitle } from "./iconVideo";
import { RefreshIcon, WebCamIcon } from "@/components/icons";
import {
  CloseIcon,
  PictureInPictureIcon,
  Icon10,
  Icon10Forward,
  PauseIcon,
  PlayIcon,
  SoundIcon,
} from "@/components/icons";
import {
  AlertCircle,
  Lock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  FastForward,
  Gauge,
  Volume2,
  VolumeX,
} from "lucide-react";
import type { TimelineClipWindow } from "./hooks/types";
import { useVideoGestures } from "./useVideoGestures";
import { usePlayerPulse } from "./hooks/usePlayerPulse";
import { PlayerPulse } from "./PlayerPulse";
import { useForwardAngle } from "./useForwardAngle";
import { dlog } from "@/lib/clientDebugLog";

// ВРЕМЕННО (диагностика «деталь чёрная, но звук»): лог при открытии fullscreen.
function fsDbgOn(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      (window.location.search.indexOf("debug=1") !== -1 ||
        window.localStorage.getItem("debug") === "1")
    );
  } catch {
    return false;
  }
}

interface VideoSource {
  src: string;
  sessionId?: number;
  duration?: number;
  isRecording?: boolean;
}

interface VideoTrack {
  id: string;
  src?: string;
  title?: string;
  additionalSources?: string[];
  durations?: number[];
  isRecording?: boolean;
  sources?: VideoSource[];
  initialSourceIndex?: number;
}


export function FullscreenVideo({
  track,
  onClose,
  videoRefs,
  getRotationForSource,
  onRotateSource,
  timeSec,
  setTimeSec,
  playing,
  setPlaying,
  rate,
  setRate,
  globalDurationSec,
  windows,
  activeClipIndex,
  brokenClips,
  onClipPick,
  mobile,
  events,
  tracks,
  onSelectTrack,
}: {
  track: VideoTrack;
  onClose: () => void;
  videoRefs?: React.MutableRefObject<Record<string, HTMLVideoElement | null>>;
  getRotationForSource?: (source: VideoSource) => number;
  onRotateSource?: (source: VideoSource) => void;
  timeSec?: number;
  setTimeSec?: (t: number) => void;
  playing?: boolean;
  setPlaying?: (p: boolean) => void;
  rate?: number;
  setRate?: (r: number) => void;
  globalDurationSec?: number;
  windows?: TimelineClipWindow[];
  activeClipIndex?: number;
  brokenClips?: Set<number>;
  onClipPick?: (clipIndex: number) => void;
  mobile?: boolean;
  events?: { atSec: number; color: string; screenshot?: string | null; label?: string }[];
  tracks?: { id: string; title?: string }[];
  onSelectTrack?: (trackId: string) => void;
}) {
  const tx = useTranslations();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const closingRef = useRef(false);
  const rotationTurnsRef = useRef(0);
  const snapshotCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [inPip, setInPip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const loaderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showSnapshot, setShowSnapshot] = useState(false);
  const [cameraMenuOpen, setCameraMenuOpen] = useState(false);
  const { pulseIcon, skipPulse, triggerPulse, triggerSkipPulse } = usePlayerPulse();
  const overlayMouseDownRef = useRef(false);
  const isRecordingTrack = track.isRecording === true;

  const normalizedSources = useMemo<VideoSource[]>(() => {
    if (track.sources && track.sources.length > 0) return track.sources;
    const fallbackSources = [track.src, ...(track.additionalSources || [])].filter(
      (src): src is string => typeof src === "string" && src.length > 0
    );
    return fallbackSources.map((src, index) => ({
      src,
      duration: track.durations?.[index],
      isRecording: track.isRecording,
    }));
  }, [track.additionalSources, track.durations, track.isRecording, track.sources, track.src]);

  const isControlled = timeSec !== undefined;

  const synthesizedWindows = useMemo<TimelineClipWindow[]>(() => {
    let acc = 0;
    return normalizedSources.map((src, idx) => {
      const dur = src.duration ?? 0;
      const w: TimelineClipWindow = {
        webinar_session_id: src.sessionId ?? 0,
        src: src.src,
        startSec: acc,
        endSec: acc + dur,
        durationSec: dur,
        raw: null,
        clipIndex: idx,
        isRecording: src.isRecording ?? false,
      };
      acc += dur;
      return w;
    });
  }, [normalizedSources]);

  const synthesizedGlobalDurationSec = useMemo(
    () => synthesizedWindows.reduce((sum, w) => sum + w.durationSec, 0),
    [synthesizedWindows]
  );

  const initialClipIndex = Math.max(track.initialSourceIndex ?? 0, 0);
  const [localActiveClipIndex, setLocalActiveClipIndex] = useState(initialClipIndex);
  const [localTimeSec, setLocalTimeSec] = useState(() => {
    const w = synthesizedWindows[initialClipIndex];
    return w?.startSec ?? 0;
  });
  const [localPlaying, setLocalPlaying] = useState(true);
  const [localRate, setLocalRate] = useState(1);

  const effectiveTimeSec = timeSec !== undefined ? timeSec : localTimeSec;
  const effectiveSetTimeSec = setTimeSec ?? setLocalTimeSec;
  const effectivePlaying = playing !== undefined ? playing : localPlaying;
  const effectiveSetPlaying = setPlaying ?? setLocalPlaying;
  const effectiveRate = rate !== undefined ? rate : localRate;
  const effectiveSetRate = setRate ?? setLocalRate;
  const effectiveWindows = windows ?? synthesizedWindows;
  const effectiveActiveClipIndex = activeClipIndex !== undefined ? activeClipIndex : localActiveClipIndex;
  const effectiveGlobalDurationSec = globalDurationSec !== undefined ? globalDurationSec : synthesizedGlobalDurationSec;
  const playbackStateRef = useRef({
    timeSec: effectiveTimeSec,
    playing: effectivePlaying,
    muted,
  });
  playbackStateRef.current = {
    timeSec: effectiveTimeSec,
    playing: effectivePlaying,
    muted,
  };

  const effectiveOnClipPick = onClipPick ?? ((idx: number) => {
    setLocalActiveClipIndex(idx);
    const w = synthesizedWindows[idx];
    if (w) setLocalTimeSec(w.startSec);
  });

  const clampedClipIndex = Math.min(Math.max(effectiveActiveClipIndex, 0), Math.max(normalizedSources.length - 1, 0));
  const currentSource = normalizedSources[clampedClipIndex] ?? normalizedSources[0];
  const currentWindow = effectiveWindows[clampedClipIndex];
  const isCurrentClipBroken = !isRecordingTrack
    && (brokenClips?.has(clampedClipIndex) ?? false);

  const currentRotation = currentSource && getRotationForSource
    ? getRotationForSource(currentSource)
    : 0;
  const fsAngle = useForwardAngle(currentRotation);

  const fullscreenVideoClassName = "w-full h-full object-contain transition-transform duration-300";
  const wallVideoClassName = "absolute inset-0 w-full h-full object-contain transition-transform duration-300";

  const localT = currentWindow
    ? Math.max(0, Math.min(effectiveTimeSec - currentWindow.startSec, currentWindow.durationSec || effectiveTimeSec))
    : 0;

  useEffect(() => {
    rotationTurnsRef.current = fsAngle;
  }, [fsAngle]);

  const getVideoEl = useCallback((): HTMLVideoElement | null => {
    if (isRecordingTrack && videoRefs) {
      return videoRefs.current[track.id] ?? null;
    }
    return localVideoRef.current;
  }, [isRecordingTrack, track.id, videoRefs]);

  const handleClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isRecordingTrack || !videoRefs) return;

    const videoEl = getVideoEl();
    const container = containerRef.current;
    if (!videoEl || !container) return;

    const originalParent = videoEl.parentElement;
    const originalNextSibling = videoEl.nextSibling;
    const originalClassName = videoEl.className;

    videoEl.className = fullscreenVideoClassName;
    videoEl.style.transform = `rotate(${rotationTurnsRef.current}deg)`;

    container.insertBefore(videoEl, container.firstChild);

    return () => {
      videoEl.className = wallVideoClassName;
      videoEl.style.transform = `rotate(${rotationTurnsRef.current}deg)`;

      if (originalParent) {
        if (originalNextSibling) {
          originalParent.insertBefore(videoEl, originalNextSibling);
        } else {
          originalParent.appendChild(videoEl);
        }
      } else {
        videoEl.className = originalClassName;
      }
    };
  }, [
    fullscreenVideoClassName,
    getVideoEl,
    isRecordingTrack,
    track.id,
    videoRefs,
    wallVideoClassName,
  ]);

  useEffect(() => {
    if (!isRecordingTrack) return;
    const videoEl = getVideoEl();
    if (!videoEl) return;
    videoEl.className = fullscreenVideoClassName;
    videoEl.style.transform = `rotate(${fsAngle}deg)`;
  }, [fsAngle, fullscreenVideoClassName, getVideoEl, isRecordingTrack]);

  useEffect(() => {
    const v = getVideoEl();
    if (!v) return;
    v.volume = volume;
    v.muted = muted;
    v.playbackRate = effectiveRate;
  }, [getVideoEl, volume, muted, effectiveRate]);

  useEffect(() => {
    const v = getVideoEl();
    if (!v) return;
    if (isCurrentClipBroken) {
      v.pause();
      return;
    }
    if (effectivePlaying) {
      void v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [getVideoEl, effectivePlaying, isCurrentClipBroken]);

  useEffect(() => {
    if (isRecordingTrack) return;
    if (isCurrentClipBroken) return;
    const v = localVideoRef.current;
    if (!v) return;
    if (!currentWindow) return;

    const targetLocalT = Math.max(0, Math.min(effectiveTimeSec - currentWindow.startSec, currentWindow.durationSec || effectiveTimeSec));
    const drift = Math.abs((v.currentTime || 0) - targetLocalT);
    if (drift > 1.0) {
      try {
        v.currentTime = targetLocalT;
      } catch {}
    }
  }, [isRecordingTrack, isCurrentClipBroken, currentWindow, effectiveTimeSec, clampedClipIndex]);

  useEffect(() => {
    if (isRecordingTrack) return;
    if (isCurrentClipBroken) return;
    const v = localVideoRef.current;
    if (!v) return;
    if (!currentWindow) return;

    const handleTimeUpdate = () => {
      if (v.seeking) return;
      effectiveSetTimeSec(currentWindow.startSec + (v.currentTime || 0));
    };
    v.addEventListener("timeupdate", handleTimeUpdate);
    return () => v.removeEventListener("timeupdate", handleTimeUpdate);
  }, [isRecordingTrack, isCurrentClipBroken, currentWindow, effectiveSetTimeSec]);

  useEffect(() => {
    if (isRecordingTrack) return;
    const v = localVideoRef.current;
    if (!v) return;

    const handleEnded = () => {
      if (isControlled) {
        // Controlled: nudge global time past the current clip so the parent
        // controller advances the active clip index.
        if (currentWindow) effectiveSetTimeSec(currentWindow.endSec + 0.05);
        return;
      }
      const next = clampedClipIndex + 1;
      if (next < normalizedSources.length) {
        setLocalActiveClipIndex(next);
        const w = synthesizedWindows[next];
        if (w) setLocalTimeSec(w.startSec);
      } else {
        setLocalPlaying(false);
      }
    };
    v.addEventListener("ended", handleEnded);
    return () => v.removeEventListener("ended", handleEnded);
  }, [isControlled, isRecordingTrack, clampedClipIndex, normalizedSources.length, synthesizedWindows, currentWindow, effectiveSetTimeSec]);

  useEffect(() => {
    if (isControlled) return;
    const w = synthesizedWindows.find(
      (win) => effectiveTimeSec >= win.startSec && effectiveTimeSec < win.endSec
    );
    if (w && w.clipIndex !== localActiveClipIndex) {
      setLocalActiveClipIndex(w.clipIndex);
    }
  }, [isControlled, effectiveTimeSec, synthesizedWindows, localActiveClipIndex]);

  const captureSnapshot = useCallback(() => {
    const v = getVideoEl();
    const canvas = snapshotCanvasRef.current;
    if (!v || !canvas) return false;
    if (!v.videoWidth || !v.videoHeight) return false;
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;
    try {
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      return true;
    } catch {
      return false;
    }
  }, [getVideoEl]);

  const showLoader = useCallback(() => {
    if (loaderTimeoutRef.current) return;
    loaderTimeoutRef.current = setTimeout(() => {
      setIsLoading(true);
      loaderTimeoutRef.current = null;
    }, 150);
  }, []);

  const hideLoader = useCallback(() => {
    if (loaderTimeoutRef.current) {
      clearTimeout(loaderTimeoutRef.current);
      loaderTimeoutRef.current = null;
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isRecordingTrack) return;
    const v = localVideoRef.current;
    if (!v) return;

    const onLoadStart = () => showLoader();
    const onWaiting = () => showLoader();
    const onSeeking = () => showLoader();
    const onReady = () => hideLoader();
    const onFrameReady = () => {
      setShowSnapshot(false);
      hideLoader();
    };

    v.addEventListener("loadedmetadata", onReady);
    v.addEventListener("canplay", onReady);
    v.addEventListener("durationchange", onReady);
    v.addEventListener("seeked", onFrameReady);
    v.addEventListener("playing", onFrameReady);
    v.addEventListener("loadstart", onLoadStart);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("seeking", onSeeking);
    v.addEventListener("stalled", onWaiting);

    return () => {
      v.removeEventListener("loadedmetadata", onReady);
      v.removeEventListener("canplay", onReady);
      v.removeEventListener("durationchange", onReady);
      v.removeEventListener("seeked", onFrameReady);
      v.removeEventListener("playing", onFrameReady);
      v.removeEventListener("loadstart", onLoadStart);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("stalled", onWaiting);
    };
  }, [isRecordingTrack, clampedClipIndex, showLoader, hideLoader]);

  useEffect(() => {
    if (isRecordingTrack) return;
    if (captureSnapshot()) setShowSnapshot(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clampedClipIndex]);

  useEffect(() => {
    return () => {
      if (loaderTimeoutRef.current) clearTimeout(loaderTimeoutRef.current);
    };
  }, []);

  const togglePlayback = useCallback(() => {
    triggerPulse(effectivePlaying ? "pause" : "play");
    effectiveSetPlaying(!effectivePlaying);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setShowControls(true);
    hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
  }, [effectivePlaying, effectiveSetPlaying, triggerPulse]);

  const handleOverlayMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) overlayMouseDownRef.current = true;
  }, []);

  const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const wasOnOverlay = overlayMouseDownRef.current && e.target === e.currentTarget;
    overlayMouseDownRef.current = false;
    if (wasOnOverlay) togglePlayback();
  }, [togglePlayback]);

  useEffect(() => {
    const onMove = () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      setShowControls(true);
      hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    };
    window.addEventListener("mousemove", onMove);
    hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  const togglePip = async () => {
    const v = getVideoEl();
    if (!v) return;
    try {
      if (!inPip) {
        await v.requestPictureInPicture();
        setInPip(true);
      } else {
        await document.exitPictureInPicture();
        setInPip(false);
      }
    } catch (err) {
      console.error("PiP error:", err);
    }
  };

  const seekBy = useCallback((deltaSec: number) => {
    const next = Math.max(0, Math.min(effectiveGlobalDurationSec, effectiveTimeSec + deltaSec));
    effectiveSetTimeSec(next);
  }, [effectiveGlobalDurationSec, effectiveSetTimeSec, effectiveTimeSec]);

  const mobileBarRef = useRef<HTMLDivElement | null>(null);
  const mobileBarResume = useRef(false);
  const [scrubRatio, setScrubRatio] = useState<number | null>(null);

  const mobilePoke = useCallback(() => {
    setShowControls(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3200);
  }, []);

  const cycleMobileRate = useCallback(() => {
    const speeds = [0.5, 1, 2, 4, 8];
    const i = speeds.indexOf(effectiveRate);
    effectiveSetRate(speeds[(i + 1) % speeds.length]);
    mobilePoke();
  }, [effectiveRate, effectiveSetRate, mobilePoke]);

  const gestures = useVideoGestures({
    onTap: () => {
      togglePlayback();
      mobilePoke();
    },
    onSeek: (d) => {
      seekBy(d);
      triggerSkipPulse(d > 0 ? "forward" : "backward", 10);
      mobilePoke();
    },
    setRate: effectiveSetRate,
    boostRate: 2,
    holdMs: 700,
    onActivity: mobilePoke,
  });

  const mobileSeekFromPointer = useCallback(
    (clientX: number, phase: string) => {
      const el = mobileBarRef.current;
      if (!el || effectiveGlobalDurationSec <= 0) return;
      const rect = el.getBoundingClientRect();
      const r = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      setScrubRatio(r);
      if (phase === "up") {
        effectiveSetTimeSec(r * effectiveGlobalDurationSec);
      }
    },
    [effectiveGlobalDurationSec, effectiveSetTimeSec]
  );

  const mobileDisplayRatio =
    scrubRatio !== null
      ? scrubRatio
      : effectiveGlobalDurationSec > 0
        ? effectiveTimeSec / effectiveGlobalDurationSec
        : 0;

  const gotoEvent = useCallback(
    (dir: 1 | -1) => {
      if (!events || events.length === 0) return;
      const next =
        dir === 1
          ? events.find((e) => e.atSec > effectiveTimeSec + 0.4)
          : [...events].reverse().find((e) => e.atSec < effectiveTimeSec - 0.4);
      if (next) {
        effectiveSetTimeSec(next.atSec);
        mobilePoke();
      }
    },
    [events, effectiveTimeSec, effectiveSetTimeSec, mobilePoke]
  );

  const scrubMarker = useMemo(() => {
    if (scrubRatio === null || !events || events.length === 0 || effectiveGlobalDurationSec <= 0) {
      return null;
    }
    let best: (typeof events)[number] | null = null;
    let bestDist = Infinity;
    for (const ev of events) {
      const dist = Math.abs(ev.atSec / effectiveGlobalDurationSec - scrubRatio);
      if (dist < bestDist) {
        bestDist = dist;
        best = ev;
      }
    }
    return bestDist < 0.025 ? best : null;
  }, [scrubRatio, events, effectiveGlobalDurationSec]);

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseFloat(e.target.value);
    if (Number.isFinite(next)) effectiveSetTimeSec(next);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case " ":
          e.preventDefault();
          togglePlayback();
          break;
        case "ArrowUp":
          e.preventDefault();
          setVolume((prev) => Math.min(1, prev + 0.05));
          break;
        case "ArrowDown":
          e.preventDefault();
          setVolume((prev) => Math.max(0, prev - 0.05));
          break;
        case "ArrowRight":
          e.preventDefault();
          seekBy(10);
          triggerSkipPulse("forward", 10);
          break;
        case "ArrowLeft":
          e.preventDefault();
          seekBy(-10);
          triggerSkipPulse("backward", 10);
          break;
        case "Home":
          e.preventDefault();
          effectiveSetTimeSec(0);
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          handleClose();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [handleClose, seekBy, effectiveSetTimeSec, togglePlayback, triggerSkipPulse]);

  const localVideoSrc = !isRecordingTrack && !isCurrentClipBroken
    ? (currentWindow?.src ?? currentSource?.src)
    : undefined;

  const cameraOptions =
    tracks && tracks.length > 0 ? tracks : [{ id: track.id, title: track.title }];
  const canSwitchCamera = !!onSelectTrack && cameraOptions.length > 1;

  useEffect(() => {
    if (!fsDbgOn()) return;
    dlog(
      `[FS open] track=${track.id} isRecording=${isRecordingTrack} ` +
        `src=${String(localVideoSrc ?? "").slice(-14)} mobile=${!!mobile}`,
      "info",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
    >
      {!isRecordingTrack && (
        <video
          ref={localVideoRef}
          src={localVideoSrc}
          className={`${fullscreenVideoClassName} relative z-0`}
          style={fsAngle ? { transform: `rotate(${fsAngle}deg)` } : undefined}
          playsInline
          controls={false}
          autoPlay
          onLoadedMetadata={() => {
            const v = localVideoRef.current;
            if (!v || isCurrentClipBroken || !currentWindow) return;
            const target = Math.max(
              0,
              Math.min(
                effectiveTimeSec - currentWindow.startSec,
                currentWindow.durationSec || effectiveTimeSec
              )
            );
            try {
              if (Math.abs((v.currentTime || 0) - target) > 0.3) {
                v.currentTime = target;
              }
            } catch {}
          }}
        />
      )}

      {mobile ? (
        <div className="absolute inset-0 z-10" {...gestures.bind} />
      ) : (
        <div
          className="absolute inset-0 z-10"
          onMouseDown={handleOverlayMouseDown}
          onClick={handleOverlayClick}
        />
      )}

      <canvas
        ref={snapshotCanvasRef}
        className="absolute inset-0 w-full h-full object-contain pointer-events-none z-20"
        style={{
          display: showSnapshot ? "block" : "none",
          transform: fsAngle ? `rotate(${fsAngle}deg)` : undefined,
        }}
      />

      {isLoading && !isCurrentClipBroken && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none transition-opacity duration-150 z-30">
          <div className="w-12 h-12 rounded-full border-4 border-white/30 border-t-white animate-spin" />
        </div>
      )}

      {isCurrentClipBroken && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/85 z-35 p-4 gap-2 pointer-events-none">
          <AlertCircle className="w-12 h-12 text-red-400 flex-shrink-0" />
          <p className="text-white text-base font-medium text-center">
            {tx("label-clip-broken")}
          </p>
          <p className="text-white/60 text-sm text-center max-w-md">
            {tx("label-clip-broken-description")}
          </p>
        </div>
      )}

      <PlayerPulse pulseIcon={pulseIcon} skipPulse={skipPulse} />

      {!mobile &&
        (canSwitchCamera ? (
          <div
            className="absolute top-4 left-4 z-40"
            style={{ pointerEvents: "all" }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                type="button"
                onClick={() => setCameraMenuOpen((v) => !v)}
                className="flex cursor-pointer items-center gap-2 rounded-md border border-white/30 bg-black/60 py-1.5 pl-2.5 pr-2 text-sm font-medium text-white transition-colors hover:bg-black/80"
              >
                {React.createElement(getIconByTitle(track.title || ""), {
                  className: "w-4 h-4 shrink-0 text-white",
                })}
                <span className="max-w-[200px] truncate">
                  {resolveTrackTitle(track.id, track.title || "", tx)}
                </span>
                <ChevronDown size={16} className="shrink-0 text-white/80" />
              </button>
              {cameraMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-0"
                    onClick={() => setCameraMenuOpen(false)}
                  />
                  <div className="absolute left-0 top-full z-10 mt-1 max-h-72 w-60 overflow-y-auto rounded-md border border-white/10 bg-[#2a2a2a] py-1 shadow-2xl">
                    {cameraOptions.map((t) => {
                      const active = t.id === track.id;
                      const Icon = getIconByTitle(t.title || "");
                      return (
                        <button
                          key={t.id}
                          onClick={() => {
                            onSelectTrack?.(t.id);
                            setCameraMenuOpen(false);
                          }}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/10 ${
                            active ? "font-medium text-white" : "text-white/70"
                          }`}
                        >
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="flex-1 truncate">
                            {resolveTrackTitle(t.id, t.title || "", tx)}
                          </span>
                          {active && <Check size={16} className="shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          React.createElement(getIconByTitle(track.title || ""), {
            className:
              "absolute top-4 left-4 w-6 h-6 text-white/90 drop-shadow-md pointer-events-none z-40",
          })
        ))}

      {normalizedSources.length > 1 && (
        <div
          className={
            mobile
              ? "absolute right-3 top-14 z-[55]"
              : "absolute top-4 right-4 z-40"
          }
          style={{ pointerEvents: "all" }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative">
            <WebCamIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white" />
            <select
              value={clampedClipIndex}
              onChange={(e) => {
                e.stopPropagation();
                const idx = Number(e.target.value);
                if (Number.isFinite(idx)) effectiveOnClipPick(idx);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="bg-black/80 text-white text-xs rounded-lg py-1 pl-7 pr-6 cursor-pointer border border-white/30 outline-none hover:bg-black focus:bg-black transition-colors appearance-none"
              style={{ minWidth: 56 }}
            >
              {normalizedSources.map((_, i) => (
                <option key={i} value={i} className="bg-black text-white">
                  {i + 1}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {!mobile && (
      <div
        className={`absolute bottom-0 left-0 right-0 z-50 transition-opacity duration-500 ${
          showControls ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onMouseEnter={() => {
          if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
          setShowControls(true);
        }}
        onMouseLeave={() => {
          hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
        }}
      >
        <div className="flex items-center gap-3 px-4 py-3 bg-white backdrop-blur-sm text-black">
          <button
            onClick={togglePlayback}
            className="cursor-pointer flex items-center justify-center p-2 rounded-lg bg-muted/60 hover:bg-accent text-black"
          >
            {effectivePlaying ? (
              <PauseIcon size={16} className="w-4 h-4 text-black" />
            ) : (
              <PlayIcon size={16} fill="currentColor" className="w-4 h-4 text-black" />
            )}
          </button>

          <button
            onClick={() => {
              seekBy(-10);
              triggerSkipPulse("backward", 10);
            }}
            className="p-2 rounded-lg bg-muted/60 text-black cursor-pointer"
            aria-label="Rewind 10 seconds"
          >
            <Icon10 size={15} aria-hidden="true" />
          </button>

          <button
            onClick={() => {
              seekBy(10);
              triggerSkipPulse("forward", 10);
            }}
            className="p-2 rounded-lg bg-muted/60 text-black cursor-pointer"
            aria-label="Forward 10 seconds"
          >
            <Icon10Forward size={15} aria-hidden="true" />
          </button>

          {[0.5, 1, 2, 4, 8].map((speed) => (
            <button
              key={speed}
              onClick={() => effectiveSetRate(speed)}
              className={`px-2 py-1 rounded-lg cursor-pointer ${
                effectiveRate === speed
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/60 text-muted-foreground"
              }`}
            >
              {speed}x
            </button>
          ))}

          <input
            type="range"
            min={0}
            max={effectiveGlobalDurationSec || 0}
            value={effectiveTimeSec}
            onChange={handleProgressChange}
            className="flex-1 mx-3 accent-black cursor-pointer h-[4px]"
          />

          <div className="text-sm whitespace-nowrap">
            {fmt(effectiveTimeSec)} / {fmt(effectiveGlobalDurationSec)}
            {normalizedSources.length > 1 && (
              <span className="ml-2 text-xs text-gray-500">
                ({clampedClipIndex + 1}/{normalizedSources.length})
              </span>
            )}
            {currentWindow && (
              <span className="ml-2 text-xs text-gray-500">
                · {fmt(localT)}
              </span>
            )}
          </div>

          {currentSource && onRotateSource && (
            <button
              type="button"
              className="p-2 rounded-lg bg-muted/60 font-semibold cursor-pointer"
              aria-label="Rotate video"
              title="Rotate video"
              onClick={() => onRotateSource(currentSource)}
            >
              <RefreshIcon size={16} className="w-4 h-4 text-black" />
            </button>
          )}

          <button
            onClick={() =>
              setMuted((prev) => {
                triggerPulse(prev ? "unmute" : "mute");
                return !prev;
              })
            }
            className="p-2 ml-3 rounded-lg bg-muted/60 cursor-pointer"
            type="button"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            <span
              className={`relative block w-4 h-4 leading-none ${
                muted
                  ? "after:content-[''] after:absolute after:left-[-2px] after:right-[-2px] after:top-1/2 after:h-[2px] after:-translate-y-1/2 after:bg-black after:rotate-45 after:rounded-full"
                  : ""
              }`}
            >
              <SoundIcon size={16} fill="currentColor" className="w-4 h-4 text-black block" />
            </span>
          </button>

          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-20 accent-black h-[4px] cursor-pointer"
          />

          <button
            onClick={togglePip}
            className="p-2 bg-muted/60 rounded-lg hover:text-gray-300 cursor-pointer"
            type="button"
          >
            <PictureInPictureIcon size={16} className="w-4 h-4 text-black" />
          </button>

          <button
            onClick={handleClose}
            className="p-2 rounded-lg bg-muted/60 font-semibold cursor-pointer"
            type="button"
          >
            <CloseIcon size={16} className="w-4 h-4 text-black" />
          </button>
        </div>
      </div>
      )}

      {mobile && (gestures.holding || gestures.locked) && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[60] flex justify-center"
          style={{ paddingTop: "calc(env(safe-area-inset-top) + 56px)" }}
        >
          <div
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-base font-bold shadow-lg ${
              gestures.locked ? "bg-amber-400 text-black" : "bg-black/70 text-white"
            }`}
          >
            {gestures.locked ? <Lock size={16} /> : <FastForward size={16} />}
            {effectiveRate}x
          </div>
        </div>
      )}

      {mobile && (
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 z-50 flex items-center gap-2 px-2 text-white transition-opacity duration-300 ${
            showControls ? "opacity-100" : "opacity-0"
          }`}
          style={{ paddingTop: "calc(env(safe-area-inset-top) + 8px)", paddingBottom: 8 }}
        >
          <button
            onClick={handleClose}
            className="pointer-events-auto rounded-full p-2 active:bg-white/10"
          >
            <ChevronLeft size={24} />
          </button>
          {canSwitchCamera ? (
            <button
              type="button"
              onClick={() => setCameraMenuOpen((v) => !v)}
              className="pointer-events-auto mx-auto flex min-w-0 max-w-[80%] items-center justify-center gap-1 rounded-full bg-black/50 px-4 py-1.5 text-sm font-medium text-white backdrop-blur active:bg-black/70"
            >
              <span className="truncate">
                {resolveTrackTitle(track.id, track.title || "", tx)}
              </span>
              <ChevronDown size={16} className="shrink-0 text-white/80" />
            </button>
          ) : (
            <div className="min-w-0 flex-1 truncate text-center text-sm font-medium drop-shadow">
              {resolveTrackTitle(track.id, track.title || "", tx)}
            </div>
          )}
          {currentSource && onRotateSource ? (
            <button
              onClick={() => onRotateSource(currentSource)}
              className="pointer-events-auto rounded-full p-2 active:bg-white/10"
            >
              <RefreshIcon size={20} className="text-white" />
            </button>
          ) : (
            <div className="w-10 shrink-0" />
          )}
        </div>
      )}

      {mobile && (
        <div
          className={`absolute inset-x-0 bottom-0 z-50 transition-opacity duration-300 ${
            showControls ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <div
            className="bg-gradient-to-t from-black/85 to-transparent px-3 pt-8 text-white"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 10px)" }}
          >
            <div
              ref={mobileBarRef}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                mobileBarResume.current = effectivePlaying;
                if (effectivePlaying) effectiveSetPlaying(false);
                mobileSeekFromPointer(e.clientX, "down");
                mobilePoke();
              }}
              onPointerMove={(e) => {
                if (e.buttons > 0) mobileSeekFromPointer(e.clientX, "move");
              }}
              onPointerUp={(e) => {
                mobileSeekFromPointer(e.clientX, "up");
                setScrubRatio(null);
                if (mobileBarResume.current) effectiveSetPlaying(true);
                mobileBarResume.current = false;
              }}
              onPointerCancel={() => {
                setScrubRatio(null);
                if (mobileBarResume.current) effectiveSetPlaying(true);
                mobileBarResume.current = false;
              }}
              onContextMenu={(e) => e.preventDefault()}
              style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
              className="relative h-8 w-full cursor-pointer touch-none"
            >
              <div className="absolute top-1/2 h-1.5 w-full -translate-y-1/2 rounded-full bg-white/25" />
              <div
                className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-white"
                style={{ width: `${mobileDisplayRatio * 100}%` }}
              />
              {effectiveGlobalDurationSec > 0 &&
                events?.map((ev, i) => {
                  const left = (ev.atSec / effectiveGlobalDurationSec) * 100;
                  if (left < 0 || left > 100) return null;
                  return (
                    <span
                      key={i}
                      className="pointer-events-none absolute top-1/2 h-3 w-1 -translate-x-1/2 -translate-y-1/2 rounded-sm"
                      style={{ left: `${left}%`, background: ev.color }}
                    />
                  );
                })}
              <span
                className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-black bg-white shadow"
                style={{ left: `${mobileDisplayRatio * 100}%` }}
              />
              {scrubMarker && scrubRatio !== null && (
                <div
                  className="pointer-events-none absolute bottom-full mb-3 flex w-32 -translate-x-1/2 flex-col items-center gap-1"
                  style={{ left: `${Math.max(16, Math.min(84, scrubRatio * 100))}%` }}
                >
                  {scrubMarker.screenshot && (
                    <img
                      src={scrubMarker.screenshot}
                      alt=""
                      className="h-20 w-32 rounded-md object-cover ring-2 ring-white/80"
                    />
                  )}
                  <div className="max-w-full truncate rounded-lg bg-black/80 px-2 py-0.5 text-[11px] text-white">
                    {scrubMarker.label} · {fmt(scrubRatio * effectiveGlobalDurationSec)}
                  </div>
                </div>
              )}
            </div>

            <div className="-mt-0.5 mb-2 flex justify-between text-[11px] tabular-nums text-white/70">
              <span>{fmt(effectiveTimeSec)}</span>
              <span>{fmt(effectiveGlobalDurationSec)}</span>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => gotoEvent(-1)}
                disabled={!events || events.length === 0}
                className="flex items-center gap-1 rounded-lg px-2 py-2 text-xs text-white active:bg-white/10 disabled:opacity-30"
              >
                <ChevronLeft size={18} />
                {tx("label-log")}
              </button>
              <button
                onClick={() => {
                  togglePlayback();
                  mobilePoke();
                }}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-black active:scale-95"
              >
                {effectivePlaying ? (
                  <PauseIcon size={20} className="text-black" />
                ) : (
                  <PlayIcon size={20} className="ml-0.5 text-black" />
                )}
              </button>
              <button
                onClick={() => gotoEvent(1)}
                disabled={!events || events.length === 0}
                className="flex items-center gap-1 rounded-lg px-2 py-2 text-xs text-white active:bg-white/10 disabled:opacity-30"
              >
                {tx("label-log")}
                <ChevronRight size={18} />
              </button>
              <button
                onClick={cycleMobileRate}
                className={`flex items-center gap-1 rounded-lg px-2.5 py-2 text-sm font-semibold ${
                  gestures.locked ? "bg-amber-400 text-black" : "text-white active:bg-white/10"
                }`}
              >
                {gestures.locked ? <Lock size={14} /> : <Gauge size={16} />}
                {effectiveRate}x
              </button>
              <button
                onClick={() => {
                  setMuted((m) => !m);
                  mobilePoke();
                }}
                className="rounded-lg p-2 text-white active:bg-white/10"
              >
                {muted ? <VolumeX size={22} /> : <Volume2 size={22} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {mobile && canSwitchCamera && cameraMenuOpen && (
        <div
          className="absolute inset-0 z-[100]"
          onClick={() => setCameraMenuOpen(false)}
        >
          <div
            className="absolute left-1/2 w-64 max-w-[88%] -translate-x-1/2 overflow-y-auto rounded-2xl bg-[#1f1f1f] py-1.5 text-white shadow-2xl ring-1 ring-white/10"
            style={{
              top: "calc(env(safe-area-inset-top) + 52px)",
              maxHeight: "55%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {cameraOptions.map((t) => {
              const active = t.id === track.id;
              const Icon = getIconByTitle(t.title || "");
              return (
                <button
                  key={t.id}
                  onClick={() => {
                    onSelectTrack?.(t.id);
                    setCameraMenuOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left text-[15px] active:bg-white/10 ${
                    active ? "font-medium text-white" : "text-white/70"
                  }`}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="flex-1 truncate">
                    {resolveTrackTitle(t.id, t.title || "", tx)}
                  </span>
                  {active && <Check size={18} className="shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
